/**
 * FormSelect — antd Select 래퍼 (검색/가상스크롤/allowClear 기본 ON).
 *
 * 【역할】
 * - 프레임워크 표준 드롭다운.
 * - 프로젝트 공통 UX 기본값(allowClear / showSearch / virtual / optionFilterProp='label') 을 강제.
 * - placeholder 미지정 시 i18n 키 PH_ALL ('All') 사용.
 *
 * 【주요 기능】
 * - mode='multiple' 또는 'tags' 일 때 maxTagCount 기본 'responsive'.
 * - optionFilterProp 기본 'label' (라벨 기준 검색).
 *
 * 【Props】
 * - (antd SelectProps<T> 전부) — 일부 기본값 덮어쓰기.
 *
 * 【사용 예시】
 * ```tsx
 * <FormSelect<string> options={opts} placeholder={t('PH_SELECT')} />
 * ```
 */
import { Select } from 'antd'
import type { SelectProps } from 'antd'
import { useLabel } from '@/framework/useLabel'

/**
 * FormSelect — antd Select 래퍼 컴포넌트 (검색/가상스크롤 기본 ON).
 * @param props - antd `SelectProps<T>` 전체 (일부 기본값을 프레임워크 표준으로 덮어쓴다)
 * @returns 프레임워크 기본값이 적용된 antd Select 엘리먼트
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- antd SelectProps<T> generic 호환을 위해 any 디폴트 유지 (unknown 대체 시 호출부 타입 추론 깨짐)
export default function FormSelect<T = any>(props: SelectProps<T>) {
  const { allowClear, showSearch, optionFilterProp, virtual, maxTagCount, placeholder, ...rest } =
    props
  const { t } = useLabel()
  const isMulti = props.mode === 'multiple' || props.mode === 'tags'
  return (
    <Select<T>
      allowClear={allowClear ?? true}
      showSearch={showSearch ?? true}
      optionFilterProp={optionFilterProp ?? 'label'}
      virtual={virtual ?? true}
      maxTagCount={maxTagCount ?? (isMulti ? 'responsive' : undefined)}
      placeholder={placeholder ?? t('PH_ALL', 'All')}
      {...rest}
    />
  )
}
